import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Chip,
  makeStyles,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { spassuCodeApiRef } from '../../api';
import type { SkillSummary } from '../../api/types';
import { SkillReadError } from '../../api/skill-read';
import { STATUS_LABELS } from './skill-status';
import { SkillFormattedContent } from './SkillFormattedContent';

export interface SkillContentDialogProps {
  selection: Pick<SkillSummary, 'id' | 'name'> | null;
  onClose: () => void;
  onExited: () => void;
}

type ReadingState =
  | { kind: 'loading'; id: string }
  | { kind: 'ready'; id: string; skill: SkillSummary }
  | { kind: 'error'; id: string; error: SkillReadError };

const useStyles = makeStyles(theme => ({
  paper: { borderRadius: theme.spacing(2), height: 'min(90vh, 1000px)' },
  title: { padding: theme.spacing(2.5, 4), '& h2': { fontWeight: 600 } },
  body: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3, 4),
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) },
  },
  article: {
    maxWidth: '82ch',
    margin: '0 auto',
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(2) },
  },
  metadata: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  actions: { padding: theme.spacing(1.5, 3) },
}));

function useSkillReading(id: string | undefined, attempt: number) {
  const api = useApi(spassuCodeApiRef);
  const [state, setState] = useState<ReadingState>({ kind: 'loading', id: '' });
  useEffect(() => {
    if (!id) {
      setState({ kind: 'loading', id: '' });
      return undefined;
    }
    let active = true;
    setState({ kind: 'loading', id });
    api
      .getSkill(id)
      .then(skill => {
        if (!active) return;
        if (!skill || skill.id !== id) {
          setState({
            kind: 'error',
            id,
            error: new SkillReadError('unavailable'),
          });
          return;
        }
        setState({ kind: 'ready', id, skill });
      })
      .catch(error => {
        if (active)
          setState({
            kind: 'error',
            id,
            error:
              error instanceof SkillReadError
                ? error
                : new SkillReadError('load'),
          });
      });
    return () => {
      active = false;
    };
  }, [api, id, attempt]);
  return state.id === id ? state : null;
}

function ReadingContent({ current }: { current: ReadingState | null }) {
  const classes = useStyles();
  if (!current || current.kind === 'loading')
    return <Typography role="status">Carregando...</Typography>;
  if (current.kind === 'error')
    return (
      <Typography role="alert" color="error">
        {current.error.message}
      </Typography>
    );
  const { skill } = current;
  return (
    <article className={classes.article} aria-label="Conteúdo da skill">
      <div className={classes.metadata}>
        <Chip
          size="small"
          variant="outlined"
          label={skill.version || 'Versão não informada'}
        />
        <Chip
          size="small"
          label={
            STATUS_LABELS[skill.status] ??
            (skill.status || 'Status não informado')
          }
        />
      </div>
      {skill.content?.trim() ? (
        <SkillFormattedContent key={skill.id} content={skill.content} />
      ) : (
        <Typography>Esta skill não possui conteúdo.</Typography>
      )}
    </article>
  );
}

export function SkillContentDialog({
  selection,
  onClose,
  onExited,
}: SkillContentDialogProps) {
  const theme = useTheme();
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [attempt, setAttempt] = useState(0);
  const current = useSkillReading(selection?.id, attempt);
  const name = current?.kind === 'ready' ? current.skill.name : selection?.name;
  return (
    <Dialog
      open={Boolean(selection)}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      classes={{ paper: fullScreen ? undefined : classes.paper }}
      fullScreen={fullScreen}
      aria-labelledby="skill-content-title"
      disableRestoreFocus
      TransitionProps={{ onExited }}
    >
      <DialogTitle id="skill-content-title" className={classes.title}>
        {name}
      </DialogTitle>
      <DialogContent dividers className={classes.body}>
        <ReadingContent current={current} />
      </DialogContent>
      <DialogActions className={classes.actions}>
        {current?.kind === 'error' && current.error.kind === 'load' && (
          <Button onClick={() => setAttempt(value => value + 1)}>
            Tentar novamente
          </Button>
        )}
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
